import React from 'react';
import { useParams } from 'react-router-dom';
import { Icon } from '@fashionnovateam/nova-ui-toolkit';
import { useSignedImageUrl } from '../../../Lib/Hooks/sample-tags.hooks';
import { extractFileKey } from '../AddTags/utils/imageUtils';
import { SampleTagResponse } from '../AddTags/types';

interface SampleTagImageProps {
  productImage?: string;
  alt?: string;
  className?: string;
  fallback?: React.ReactNode;
  onError?: (e: React.SyntheticEvent<HTMLImageElement, Event>) => void;
  sampleTag?: SampleTagResponse;
}

/**
 * Component for displaying sample tag images
 * - For external URLs (http/https/blob): Uses the URL directly
 * - Prioritizes embedded signed URLs from sampleTag object
 * - Falls back to fetching signed URLs for S3 file keys
 * - Automatically handles loading states and error fallbacks
 */
export function SampleTagImage({
  productImage,
  alt = 'Sample tag image',
  className = '',
  fallback,
  onError,
  sampleTag,
}: SampleTagImageProps): React.ReactElement {
  const { id: userId } = useParams<{ id: string }>();

  const imageToUse = productImage || sampleTag?.product_image;

  const isExternalUrl =
    imageToUse &&
    (imageToUse.startsWith('https://') || imageToUse.startsWith('blob:'));

  const hasEmbeddedSignedUrl =
    sampleTag?.product_image_signed_url &&
    sampleTag?.product_image_expires_at &&
    new Date(sampleTag.product_image_expires_at) > new Date();

  const fileKey =
    isExternalUrl || hasEmbeddedSignedUrl
      ? undefined
      : extractFileKey(imageToUse);

  const shouldFetchSignedUrl =
    !!fileKey && !isExternalUrl && !hasEmbeddedSignedUrl;

  const {
    data: signedUrlData,
    isLoading,
    error,
  } = useSignedImageUrl(userId || '', fileKey, shouldFetchSignedUrl);

  if (!imageToUse) {
    if (fallback) {
      return fallback as React.ReactElement;
    }
    return (
      <div
        className={`bg-gray-200 rounded flex items-center justify-center text-[10px] text-gray-500 ${className}`}
      >
        No Image
      </div>
    );
  }

  if (isExternalUrl) {
    return (
      <img src={imageToUse} alt={alt} className={className} onError={onError} />
    );
  }

  if (hasEmbeddedSignedUrl && sampleTag?.product_image_signed_url) {
    return (
      <img
        src={sampleTag.product_image_signed_url}
        alt={alt}
        className={className}
        onError={onError}
      />
    );
  }

  if (shouldFetchSignedUrl) {
    if (error) {
      if (fallback) {
        return fallback as React.ReactElement;
      }
      return (
        <div
          className={`bg-gray-200 rounded flex items-center justify-center text-[10px] text-gray-500 ${className}`}
        >
          No Image
        </div>
      );
    }

    if (isLoading) {
      return (
        <div
          className={`bg-gray-100 rounded flex items-center justify-center ${className}`}
        >
          <Icon.LoadingCircle />
        </div>
      );
    }

    if (signedUrlData?.signedUrl) {
      return (
        <img
          src={signedUrlData.signedUrl}
          alt={alt}
          className={className}
          onError={onError}
        />
      );
    }
  }

  if (fallback) {
    return fallback as React.ReactElement;
  }
  return (
    <div
      className={`bg-gray-200 rounded flex items-center justify-center text-[10px] text-gray-500 ${className}`}
    >
      No Image
    </div>
  );
}

export default SampleTagImage;
